import { formatDateTime, groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import {
  getScanGroup,
  getScanPhotoScanVideoId,
  isLineItemScoped,
  isOptionScoped,
  type ScanGroupOptimized,
  type ScanLineItemOptimized,
  type ScanOptionOptimized,
  type ScanPhoto,
  type ScanRootOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

import {
  buildPhotoAddedToScopeMessage,
  type VideoToolboxActivityLogEntry,
} from './videoToolboxActivityLogLib';

export type VideoToolboxThreadHistoryPhotoEntry = {
  kind: 'history-photo';
  id: string;
  photoId: string;
  createdAtMs: number;
  message: string;
};

export type VideoToolboxThreadHistoryAppliedEntry = {
  kind: 'history-applied';
  id: string;
  createdAtMs: number;
  summary: string;
};

export type VideoToolboxThreadHistoryEntry =
  | VideoToolboxThreadHistoryPhotoEntry
  | VideoToolboxThreadHistoryAppliedEntry;

const getAgentPartEditedAtMs = (part: {
  agentUpdateAcceptedAt?: number;
  agentUpdatedAt?: number;
}): number | undefined => part.agentUpdateAcceptedAt ?? part.agentUpdatedAt;

const getClipStillCreatedAtMs = (photo: ScanPhoto): number => photo.addedAt ?? Math.round((photo.videoFrameTimeSeconds ?? 0) * 1000);

export const formatVideoToolboxThreadCreatedAt = (createdAtMs: number): string => formatDateTime(createdAtMs);

const isClipStillForVideo = (photo: ScanPhoto, scanVideoId: string): boolean =>
  getScanPhotoScanVideoId(photo) === scanVideoId && photo.videoFrameTimeSeconds != null;

const findPhotoAttachDisplayName = (group: ScanGroupOptimized, photoId: string): string => {
  for (const task of group.tasks) {
    const attachedToTask = (task.photos ?? []).some(photo => photo.photoId === photoId)
      || task.optimized.allPhotos.some(photo => photo.photoId === photoId);
    if (attachedToTask) return taskNamePresenter(task);
  }
  return groupNamePresenter(group);
};

const buildLineItemWorkLabel = (option: ScanOptionOptimized, lineItem?: ScanLineItemOptimized): string => {
  if (!lineItem) return option.template.name;
  return lineItem.template.name !== option.template.name
    ? `${option.template.name} › ${lineItem.template.name}`
    : option.template.name;
};

/** Builds the v2g-act summary line for an agent-edited task from current scan shape. */
export const buildAgentEditedTaskSummary = (task: ScanTaskOptimized): string => {
  const taskName = taskNamePresenter(task);
  const scopedOption = task.options.find(isOptionScoped) ?? task.options[0];
  if (!scopedOption) {
    return task.userAdded ? `Add: ${taskName}` : `Update: ${taskName}`;
  }
  const scopedLineItem = scopedOption.lineItems.find(isLineItemScoped) ?? scopedOption.lineItems[0];
  if (task.userAdded || isOptionScoped(scopedOption)) {
    return `Add: ${taskName} › ${buildLineItemWorkLabel(scopedOption, scopedLineItem)}`;
  }
  if (scopedLineItem) {
    return `Update: ${taskName}: ${scopedLineItem.template.name}`;
  }
  return `Update: ${taskName}`;
};

const buildAgentEditedOptionSummary = (task: ScanTaskOptimized, option: ScanOptionOptimized): string => {
  const taskName = taskNamePresenter(task);
  const lineItem = option.lineItems.find(isLineItemScoped) ?? option.lineItems[0];
  return `Add: ${taskName} › ${buildLineItemWorkLabel(option, lineItem)}`;
};

const collectClipStillPhotos = (group: ScanGroupOptimized, scanVideoId: string): ScanPhoto[] => {
  const photosById = new Map<string, ScanPhoto>();
  const rememberPhoto = (photo: ScanPhoto) => {
    if (isClipStillForVideo(photo, scanVideoId)) photosById.set(photo.photoId, photo);
  };

  for (const photo of group.optimized.allPhotos ?? []) rememberPhoto(photo);
  for (const task of group.tasks) {
    for (const photo of task.optimized.allPhotos ?? []) rememberPhoto(photo);
  }

  return [...photosById.values()];
};

const collectClipStillPhotoEntries = (
  group: ScanGroupOptimized,
  scanVideoId: string,
): VideoToolboxThreadHistoryPhotoEntry[] =>
  collectClipStillPhotos(group, scanVideoId)
    .map(photo => ({
      kind: 'history-photo' as const,
      id: `history-photo-${photo.photoId}`,
      photoId: photo.photoId,
      createdAtMs: getClipStillCreatedAtMs(photo),
      message: buildPhotoAddedToScopeMessage(findPhotoAttachDisplayName(group, photo.photoId)),
    }));

const collectAgentEditedPartEntries = (
  group: ScanGroupOptimized,
): VideoToolboxThreadHistoryAppliedEntry[] => {
  const entries: VideoToolboxThreadHistoryAppliedEntry[] = [];

  for (const task of group.tasks) {
    const taskEditedAtMs = getAgentPartEditedAtMs(task);
    if (taskEditedAtMs != null) {
      entries.push({
        kind: 'history-applied',
        id: `history-applied-task-${task.id}`,
        createdAtMs: taskEditedAtMs,
        summary: buildAgentEditedTaskSummary(task),
      });
      continue;
    }

    for (const option of task.options) {
      const optionEditedAtMs = getAgentPartEditedAtMs(option);
      if (optionEditedAtMs == null) continue;
      entries.push({
        kind: 'history-applied',
        id: `history-applied-option-${option.id}`,
        createdAtMs: optionEditedAtMs,
        summary: buildAgentEditedOptionSummary(task, option),
      });
    }
  }

  return entries;
};

/** Reloads persisted clip photos and agent-edited scan parts for the open group, oldest first. */
export const collectVideoToolboxThreadHistory = (
  scanRoot: ScanRootOptimized | undefined,
  groupId: string | undefined,
  scanVideoId: string | undefined,
): VideoToolboxThreadHistoryEntry[] => {
  if (!scanRoot || !groupId || !scanVideoId) return [];

  const group = getScanGroup(scanRoot, groupId);
  return [
    ...collectClipStillPhotoEntries(group, scanVideoId),
    ...collectAgentEditedPartEntries(group),
  ].sort((left, right) => left.createdAtMs - right.createdAtMs);
};

/** Session log rows that are already represented by persisted scan provenance. */
export const isPersistedVideoToolboxActivityEntry = (entry: VideoToolboxActivityLogEntry): boolean => entry.kind === 'photo' || entry.kind === 'voice-applied';
